import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { AuthContextType, User } from '../types';

// Extended JWT payload interface
interface CustomJwtPayload extends JwtPayload {
  sub: string;
  isAdmin: boolean;
  isVerified?: boolean;
  subscriberId?: string;
  entityId?: string;
  exp: number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token) as CustomJwtPayload;
        const currentTime = Date.now() / 1000;

        if (decoded.exp && decoded.exp < currentTime) {
          // Token is expired
          logout();
        } else if (decoded.sub) {
          setUser({
            email: decoded.sub,
            isAdmin: decoded.isAdmin || false,
            isVerified: decoded.isVerified || false,
            subscriberId: decoded.subscriberId,
            entityId: decoded.entityId
          });
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        logout();
      }
    }
    setLoading(false);
  }, [token]);

  const login = (newToken: string): void => {
    localStorage.setItem('token', newToken);
    setToken(newToken);

    // Immediately decode and set user state
    try {
      const decoded = jwtDecode(newToken) as CustomJwtPayload;
      if (decoded.sub) {
        setUser({
          email: decoded.sub,
          isAdmin: decoded.isAdmin || false,
          isVerified: decoded.isVerified || false,
          subscriberId: decoded.subscriberId,
          entityId: decoded.entityId
        });
      }
    } catch (error) {
      console.error('Error decoding token during login:', error);
    }
  };

  const logout = (): void => {
    localStorage.removeItem('token');
    localStorage.removeItem('user-selected-language');
    setToken(null);
    setUser(null);
  };

  const value: AuthContextType = {
    token,
    user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
